import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Store reference - will be set after store is created
let storeRef: any = null;
let authActions: {
  logout: any;
  loginSuccess: any;
} = {
  logout: null,
  loginSuccess: null,
};

// Function to inject store and actions
export const injectStore = (store: any, actions: { logout: any; loginSuccess: any }) => {
  storeRef = store;
  authActions = actions;
};

// Create axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token refresh state
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Auth endpoints that should not trigger token refresh on 401
const AUTH_ENDPOINTS = [
  '/admin/auth/login',
  '/admin/auth/refresh-token',
  '/admin/auth/forgot-password',
  '/admin/auth/reset-password',
  '/admin/auth/verify-2fa',
];

/**
 * Check if the request URL is an auth endpoint that should skip token refresh
 */
const isAuthEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

// Request interceptor
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (storeRef) {
      const state = storeRef.getState();
      const token = state.auth.token;

      console.log('[AxiosInstance] Request:', config.method?.toUpperCase(), config.url, 'token:', token ? token.substring(0, 20) + '...' : 'NONE');

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      console.warn('[AxiosInstance] Request: storeRef is NULL - no token attached');
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Normalize paginated responses from backend format:
    //   { success, data: [...], pagination: { total, page, limit, totalPages } }
    // to frontend expected format:
    //   { success, data: { items: [...], total, page, limit, totalPages } }
    const d = response.data;
    if (d && d.success && Array.isArray(d.data) && d.pagination) {
      response.data = {
        success: d.success,
        data: {
          items: d.data,
          total: d.pagination.total ?? d.data.length,
          page: d.pagination.page ?? 1,
          pageSize: d.pagination.limit ?? d.pagination.pageSize ?? 10,
          totalPages: d.pagination.totalPages ?? 1,
        },
        message: d.message,
      };
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Log ALL errors for debugging
    console.log('[AxiosInstance] Response error:', {
      url: originalRequest.url,
      status: error.response?.status,
      data: error.response?.data,
      _retry: originalRequest._retry,
    });

    // Handle 401 Unauthorized
    // Skip token refresh for auth endpoints - just return the error
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't attempt token refresh for auth endpoints
      if (isAuthEndpoint(originalRequest.url)) {
        const authResponseData = error.response?.data as {
          message?: string;
          error?: string;
          errors?: unknown;
          code?: string;
        };
        const errorResponse = {
          status: 401,
          message:
            authResponseData?.error ||
            authResponseData?.message ||
            'Authentication failed',
          code: authResponseData?.code,
          errors: authResponseData?.errors,
        };
        return Promise.reject(errorResponse);
      }

      if (isRefreshing) {
        // Queue requests while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

        console.log('[AxiosInstance] ========== TOKEN REFRESH START ==========');
        console.log('[AxiosInstance] Current refreshToken exists:', !!refreshToken);

        if (!refreshToken) {
          console.error('[AxiosInstance] No refresh token available in localStorage or sessionStorage');
          throw new Error('No refresh token available');
        }

        console.log('[AxiosInstance] Calling refresh-token API...');
        const response = await axios.post(`${API_BASE_URL}/admin/auth/refresh-token`, {
          refreshToken,
        });

        console.log('[AxiosInstance] ========== REFRESH RESPONSE ==========');
        console.log('[AxiosInstance] Full response.data:', response.data);
        console.log('[AxiosInstance] response.data.success:', response.data?.success);
        console.log('[AxiosInstance] response.data.data:', response.data?.data);
        console.log('[AxiosInstance] response.data.data.tokens:', response.data?.data?.tokens);

        // Handle the response structure: { success, data: { tokens: { accessToken, refreshToken } } }
        const tokens = response.data?.data?.tokens;

        if (!tokens) {
          console.error('[AxiosInstance] ERROR: tokens object is missing from response!');
          console.error('[AxiosInstance] Expected path: response.data.data.tokens');
          throw new Error('Invalid refresh response - no tokens object');
        }

        if (!tokens.accessToken) {
          console.error('[AxiosInstance] ERROR: accessToken is missing from tokens!');
          console.error('[AxiosInstance] tokens object:', tokens);
          throw new Error('Invalid refresh response - no accessToken');
        }

        const newAccessToken = tokens.accessToken;
        const newRefreshToken = tokens.refreshToken;

        console.log('[AxiosInstance] ========== SAVING NEW TOKENS ==========');
        console.log('[AxiosInstance] BEFORE - accessToken in localStorage:', localStorage.getItem('accessToken')?.substring(0, 20) || 'NULL');
        console.log('[AxiosInstance] BEFORE - refreshToken in localStorage:', localStorage.getItem('refreshToken')?.substring(0, 20) || 'NULL');

        // Save new access token to localStorage
        console.log('[AxiosInstance] Saving new accessToken:', newAccessToken.substring(0, 30) + '...');
        localStorage.setItem('accessToken', newAccessToken);

        // Save new refresh token to localStorage
        if (newRefreshToken) {
          console.log('[AxiosInstance] Saving new refreshToken:', newRefreshToken.substring(0, 30) + '...');
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        // Verify tokens were saved
        console.log('[AxiosInstance] ========== VERIFICATION ==========');
        console.log('[AxiosInstance] AFTER - accessToken in localStorage:', localStorage.getItem('accessToken')?.substring(0, 20) || 'NULL');
        console.log('[AxiosInstance] AFTER - refreshToken in localStorage:', localStorage.getItem('refreshToken')?.substring(0, 20) || 'NULL');

        const verifiedAccessToken = localStorage.getItem('accessToken');
        if (!verifiedAccessToken) {
          console.error('[AxiosInstance] CRITICAL: accessToken was NOT saved to localStorage!');
          throw new Error('Failed to save new access token');
        }

        console.log('[AxiosInstance] ========== TOKEN REFRESH SUCCESS ==========');

        // Update Redux store token if available
        if (storeRef && authActions.loginSuccess) {
          const currentState = storeRef.getState();
          if (currentState.auth.admin) {
            storeRef.dispatch(authActions.loginSuccess({
              admin: currentState.auth.admin,
              token: newAccessToken
            }));
            console.log('[AxiosInstance] Redux store updated with new token');
          }
        }

        // Process queued requests with new token
        processQueue(null, newAccessToken);

        // Update the original request with new token and retry
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        console.log('[AxiosInstance] Retrying original request with new token...');
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('[AxiosInstance] Token refresh failed:', refreshError);
        processQueue(refreshError as AxiosError, null);
        // Don't force logout or redirect here — let React auth flow handle it
        // via checkAuth rejection. Only reject the promise so callers can handle.
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    // Backend may return error message in either 'error' or 'message' field
    const responseData = error.response?.data as {
      message?: string;
      error?: string;
      errors?: unknown;
      code?: string;
    };

    const errorResponse = {
      status: error.response?.status || 500,
      message:
        responseData?.error ||
        responseData?.message ||
        error.message ||
        'An unexpected error occurred',
      code: responseData?.code,
      errors: responseData?.errors,
    };

    return Promise.reject(errorResponse);
  }
);

export default axiosInstance;

// Helper types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: {
    items: T[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  message?: string;
}

export interface ApiError {
  status: number;
  message: string;
  errors?: Record<string, string[]>;
}

// Query params helper
export interface QueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: unknown;
}

export const buildQueryString = (params: QueryParams): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });

  return searchParams.toString();
};
